import React, { useEffect, useState } from 'react';
import { Table, Title, Container, LoadingOverlay, Text, Button, Anchor, Modal, TextInput, Textarea, Group, ActionIcon, Paper, Stack, Select } from '@mantine/core';
import { Link, useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { IconEdit, IconTrash, IconChevronDown, IconChevronRight, IconQrcode } from '@tabler/icons-react';
import Layout from '../components/Layout';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

interface Restaurant {
  id: string;
  name: string;
  adminNotes: string;
  menus: Menu[];
  description: string;
  querySetup?: {
    modelName?: string;
    configurationId?: string;
  };
}

interface Configuration {
  id: string;
  name: string;
  description: string;
  modelName: string;
}

interface Model {
  value: string;
  label: string;
}

interface Menu {
  id: string;
  name: string;
  image: string;
  adminNotes: string;
  createdAt: string;
  updatedAt: string;
  qrcode?: string; // Base64 encoded QR code
}

interface DeletedItems {
  restaurant: number;
  menus: number;
  menuPages: number;
  menuItems: number;
  queryLogs?: number;
}

const Restaurants: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const navigate = useNavigate();
  
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [expandedRestaurantId, setExpandedRestaurantId] = useState<string | null>(null);
  
  // Restaurant edit/delete state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState<Restaurant | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editAdminNotes, setEditAdminNotes] = useState('');
  const [editQuerySetup, setEditQuerySetup] = useState({
    modelName: '',
    configurationId: ''
  });
  
  // Configuration and model options
  const [configurations, setConfigurations] = useState<Configuration[]>([]);
  const [models, setModels] = useState<Model[]>([]);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [restaurantToDelete, setRestaurantToDelete] = useState<Restaurant | null>(null);
  
  // Menu delete state
  const [menuDeleteModalOpen, setMenuDeleteModalOpen] = useState(false);
  const [menuToDelete, setMenuToDelete] = useState<Menu | null>(null);

  const fetchRestaurants = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Fetching restaurants from API...');
      const response = await api.get('/restaurants');
      console.log('API Response:', response.data);

      // Handle both possible response structures:
      // 1) paginated shape: { data: [...] }
      // 2) legacy shape: [...]
      // 3) legacy shape: { restaurants: [...] }
      const restaurantsData = Array.isArray(response.data)
        ? response.data
        : (response.data?.data || response.data?.restaurants || []);
      
      // Ensure each restaurant has a menus array
      const restaurantsWithMenus = restaurantsData.map((restaurant: Restaurant) => ({
        ...restaurant,
        menus: Array.isArray(restaurant.menus) ? restaurant.menus : []
      }));
      
      setRestaurants(restaurantsWithMenus);
    } catch (error) {
      console.error('Error fetching restaurants:', error);
      setError(error instanceof Error ? error.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRestaurants();
    if (isAdmin) {
      fetchConfigurations();
    } else {
      setConfigurations([]);
      setModels([]);
    }
  }, [isAdmin]);

  const fetchConfigurations = async () => {
    try {

      const response = await api.get('/restaurants/configurations');
      setConfigurations(response.data.configurations);
      setModels(response.data.models);
    } catch (error) {
      console.error('Error fetching configurations:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to load configuration options',
        color: 'red'
      });
    }
  };

  const toggleRestaurant = (restaurantId: string) => {
    setExpandedRestaurantId(expandedRestaurantId === restaurantId ? null : restaurantId);
  };

  // Restaurant edit/delete handlers
  const handleEditClick = (restaurant: Restaurant) => {
    setSelectedRestaurant(restaurant);
    setEditName(restaurant.name);
    setEditDescription(restaurant.description || '');
    setEditAdminNotes(restaurant.adminNotes || '');
    setEditQuerySetup({
      modelName: restaurant.querySetup?.modelName || '',
      configurationId: restaurant.querySetup?.configurationId || ''
    });
    setEditModalOpen(true);
  };

  const handleUpdate = async () => {
    if (!selectedRestaurant) return;
    
    try {
      // Only include querySetup if at least one field is filled
      const querySetup = (editQuerySetup.modelName || editQuerySetup.configurationId) 
        ? editQuerySetup 
        : null;
      
      await api.put(`/restaurants/${selectedRestaurant.id}`, {
        name: editName,
        description: editDescription,
        adminNotes: editAdminNotes,
        querySetup
      });
      
      notifications.show({
        title: 'Success',
        message: 'Restaurant updated successfully',
        color: 'green'
      });
      
      setEditModalOpen(false);
      fetchRestaurants();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to update restaurant',
        color: 'red'
      });
    }
  };

  const handleDeleteClick = (restaurant: Restaurant) => {
    setRestaurantToDelete(restaurant);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!restaurantToDelete) return;
    
    try {
      const response = await api.delete(`/restaurants/${restaurantToDelete.id}`);
      const deletedItems: DeletedItems = response.data.deletedItems;
      
      notifications.show({
        title: 'Success',
        message: `Restaurant deleted successfully. Also deleted: ${deletedItems.menus} menus, ${deletedItems.menuPages} menu pages, and ${deletedItems.menuItems} menu items.`,
        color: 'green'
      });
      
      setDeleteModalOpen(false);
      fetchRestaurants();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to delete restaurant',
        color: 'red'
      });
    }
  };

  // Menu edit/delete handlers
  const handleMenuEditClick = (menu: Menu) => {
    navigate(`/menus/${menu.id}/edit`);
  };

  const handleMenuDeleteClick = (menu: Menu) => {
    setMenuToDelete(menu);
    setMenuDeleteModalOpen(true);
  };

  // QR code handler - fetches the menu's standalone QR code and opens it in a new window/tab
  const handleQrCodeClick = async (menu: Menu) => {
    // Open the window synchronously so popup blockers don't interfere,
    // then fill it in once the QR code has been fetched.
    const newWindow = window.open('', '_blank');

    let qrcode: string | null = menu.qrcode || null;
    if (!qrcode) {
      try {
        const response = await api.get(`/qrcodes/for-menu/${menu.id}`);
        qrcode = response.data?.qrCode?.image || null;
      } catch {
        qrcode = null;
      }
    }

    if (qrcode) {
      if (newWindow) {
        newWindow.document.write(`
          <html>
            <head>
              <title>QR Code - ${menu.name || 'Menu'}</title>
              <style>
                body { 
                  margin: 0; 
                  padding: 20px; 
                  font-family: Arial, sans-serif; 
                  text-align: center;
                  background-color: #f5f5f5;
                }
                .qr-container {
                  background: white;
                  padding: 20px;
                  border-radius: 8px;
                  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
                  display: inline-block;
                  margin: 20px auto;
                }
                img { 
                  max-width: 300px; 
                  height: auto; 
                  border: 1px solid #ddd;
                  border-radius: 4px;
                }
                h2 { color: #333; margin-bottom: 10px; }
                p { color: #666; margin-top: 10px; }
              </style>
            </head>
            <body>
              <div class="qr-container">
                <h2>Menu QR Code</h2>
                <img src="data:image/png;base64,${qrcode}" alt="Menu QR Code" />
                <p>Scan this QR code to access the menu on your mobile device</p>
              </div>
            </body>
          </html>
        `);
        newWindow.document.close();
      }
    } else {
      newWindow?.close();
      notifications.show({
        title: 'No QR Code',
        message: 'This menu does not have a QR code generated yet.',
        color: 'yellow'
      });
    }
  };

  const handleMenuDelete = async () => {
    if (!menuToDelete) return;
    
    try {
      const response = await api.delete(`/menus/${menuToDelete.id}`);
      
      if (response.status === 200) {
        const deletedItems = response.data.deletedItems;
        const message = deletedItems.queryLogs > 0 
          ? `Menu deleted successfully. Also deleted: ${deletedItems.menuPages} menu pages, ${deletedItems.menuItems} menu items, and ${deletedItems.queryLogs} query logs.`
          : `Menu deleted successfully. Also deleted: ${deletedItems.menuPages} menu pages and ${deletedItems.menuItems} menu items.`;
        
        notifications.show({
          title: 'Success',
          message: message,
          color: 'green',
        });
        
        // Refresh the restaurants list
        fetchRestaurants();
      }
    } catch (error) {
      console.error('Error deleting menu:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to delete menu',
        color: 'red',
      });
    } finally {
      setMenuDeleteModalOpen(false);
      setMenuToDelete(null);
    }
  };

  if (loading) return <LoadingOverlay visible={loading} />;
  if (error) return <Text color="red">{error}</Text>;

  return (
    <Layout>
      <Container>
        <Title order={1} mb="md">
          Restaurants
        </Title>
        
        {error && (
          <Text color="red" mb="md">
            Error: {error}
          </Text>
        )}
        
        <div style={{ position: 'relative' }}>
          <LoadingOverlay visible={loading} />
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Description</Table.Th>
                <Table.Th>Admin Notes</Table.Th>
                {isAdmin && <Table.Th>Actions</Table.Th>}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {restaurants.length > 0 ? (
                restaurants.map((restaurant) => (
                  <React.Fragment key={restaurant.id}>
                    <Table.Tr>
                      <Table.Td>
                        <Group gap={0}>
                          <ActionIcon 
                            variant="subtle" 
                            onClick={() => toggleRestaurant(restaurant.id)}
                          >
                            {expandedRestaurantId === restaurant.id ? 
                              <IconChevronDown size={16} /> : 
                              <IconChevronRight size={16} />
                            }
                          </ActionIcon>
                          <Button 
                            variant="subtle" 
                            onClick={() => toggleRestaurant(restaurant.id)}
                            style={{ padding: 0, height: 'auto' }}
                          >
                            {restaurant.name}
                          </Button>
                        </Group>
                      </Table.Td>
                      <Table.Td>{restaurant.description}</Table.Td>
                      <Table.Td>{restaurant.adminNotes}</Table.Td>
                      {isAdmin && (
                        <Table.Td>
                          <Group gap={0}>
                            <ActionIcon 
                              variant="subtle" 
                              color="green" 
                              onClick={() => handleEditClick(restaurant)}
                            >
                              <IconEdit size={16} />
                            </ActionIcon>
                            <ActionIcon 
                              variant="subtle" 
                              color="red" 
                              onClick={() => handleDeleteClick(restaurant)}
                            >
                              <IconTrash size={16} />
                            </ActionIcon>
                          </Group>
                        </Table.Td>
                      )}
                    </Table.Tr>
                    {expandedRestaurantId === restaurant.id && restaurant.menus && (
                      <Table.Tr>
                        <Table.Td colSpan={isAdmin ? 4 : 3}>
                          <Paper p="md" withBorder>
                            <Title order={4} mb="md">Menus</Title>
                            {restaurant.menus && restaurant.menus.length > 0 ? (
                              <Table>
                                <Table.Thead>
                                  <Table.Tr>
                                    <Table.Th>Created</Table.Th>
                                    <Table.Th>Updated</Table.Th>
                                    {isAdmin && <Table.Th>Actions</Table.Th>}
                                  </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                  {restaurant.menus && restaurant.menus.map((menu) => (
                                    <Table.Tr key={menu.id}>
                                      <Table.Td>
                                        {/* Some legacy menus may not have createdAt in list payloads, so guard date formatting. */}
                                        {menu.createdAt ? (
                                        <Anchor component={Link} to={`/menus/v2/${menu.id}`}>
                                          {new Date(menu.createdAt).toISOString().split('T')[0]}
                                        </Anchor>
                                        ) : (
                                          <Anchor component={Link} to={`/menus/v2/${menu.id}`}>
                                            {menu.id}
                                          </Anchor>
                                        )}
                                      </Table.Td>
                                      <Table.Td>
                                        {menu.updatedAt ? new Date(menu.updatedAt).toISOString().split('T')[0] : '—'}
                                      </Table.Td>
                                      {isAdmin && (
                                        <Table.Td>
                                          <Group gap="xs" justify="flex-end">
                                            <ActionIcon 
                                              variant="subtle" 
                                              color="green" 
                                              onClick={() => handleMenuEditClick(menu)}
                                            >
                                              <IconEdit size={16} />
                                            </ActionIcon>
                                            <ActionIcon 
                                              variant="subtle" 
                                              color="green" 
                                              onClick={() => handleQrCodeClick(menu)}
                                              title="View QR Code"
                                            >
                                              <IconQrcode size={16} />
                                            </ActionIcon>
                                            <ActionIcon 
                                              variant="subtle" 
                                              color="red" 
                                              onClick={() => handleMenuDeleteClick(menu)}
                                            >
                                              <IconTrash size={16} />
                                            </ActionIcon>
                                          </Group>
                                        </Table.Td>
                                      )}
                                    </Table.Tr>
                                  ))}
                                </Table.Tbody>
                              </Table>
                            ) : (
                              <Text color="dimmed">No menus found for this restaurant.</Text>
                            )}
                          </Paper>
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <Table.Tr>
                  <Table.Td colSpan={isAdmin ? 4 : 3} style={{ textAlign: 'center' }}>
                    No restaurants found
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </div>

        {/* Restaurant Edit Modal */}
        <Modal
          opened={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          title="Edit Restaurant"
          size="lg"
        >
          <Stack>
            <TextInput
              label="Name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              required
            />
            <Textarea
              label="Description"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
            />
            <Textarea
              label="Admin Notes"
              value={editAdminNotes}
              onChange={(e) => setEditAdminNotes(e.target.value)}
            />
            
            {/* Query Setup Section */}
            <Paper p="md" withBorder>
              <Title order={4} mb="md">AI Query Configuration</Title>
              <Text size="sm" color="dimmed" mb="md">
                Configure AI model and restaurant type for menu queries. Leave empty to use defaults.
              </Text>
              
              <Stack>
                <Select
                  label="AI Model"
                  placeholder="Select AI model"
                  data={models.map(model => ({ value: model.value, label: model.label }))}
                  value={editQuerySetup.modelName}
                  onChange={(value) => setEditQuerySetup(prev => ({ ...prev, modelName: value || '' }))}
                  clearable
                />
                <Select
                  label="Restaurant Configuration"
                  placeholder="Select restaurant type configuration"
                  data={configurations.map(config => ({ 
                    value: config.id, 
                    label: config.name,
                    description: config.description 
                  }))}
                  value={editQuerySetup.configurationId}
                  onChange={(value) => setEditQuerySetup(prev => ({ ...prev, configurationId: value || '' }))}
                  clearable
                />
              </Stack>
            </Paper>
            
            <Group justify="flex-end">
              <Button variant="subtle" onClick={() => setEditModalOpen(false)}>Cancel</Button>
              <Button onClick={handleUpdate}>Save</Button>
            </Group>
          </Stack>
        </Modal>

        {/* Restaurant Delete Modal */}
        <Modal
          opened={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          title="Delete Restaurant"
        >
          <Text mb="md">
            Are you sure you want to delete {restaurantToDelete?.name}? This action cannot be undone.
          </Text>
          <Text size="sm" color="red" mb="md">
            This will also delete all associated menus, menu pages, and menu items.
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
            <Button color="red" onClick={handleDelete}>Delete</Button>
          </Group>
        </Modal>

        {/* Menu Delete Confirmation Modal */}
        <Modal
          opened={menuDeleteModalOpen}
          onClose={() => setMenuDeleteModalOpen(false)}
          title="Delete Menu"
        >
          <Text>Are you sure you want to delete this menu? This action cannot be undone.</Text>
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={() => setMenuDeleteModalOpen(false)}>Cancel</Button>
            <Button color="red" onClick={handleMenuDelete}>Delete</Button>
          </Group>
        </Modal>

      </Container>
    </Layout>
  );
};

export default Restaurants; 